// Live on-garment preview (W4 phase 2): pure geometry for compositing
// the customer's design onto the calibrated placement photo.
//
// COORDINATE SPACES
//   photo-mm: the SVG overlay's viewBox. Width = calibration.photoWidthMm
//     (the real-world width of the WHOLE photo frame); height is derived
//     from the photo's natural aspect ratio, so 1 SVG unit = 1 real mm on
//     the garment.
//   zone: the calibrated box (admin phase 1) mapped from its normalised
//     0..1 fractions into photo-mm. x/y = TOP-LEFT corner, matching
//     app/lib/calibration.ts.
//   offset: the customer's drag position, in mm relative to the ZONE
//     CENTRE (x grows right, y grows down). {0,0} = centred in the zone.
//
// SIZE-IS-THE-NUMBER: nothing in here ever changes sizeMm. The photo only
// supplies the mm-per-px scale (pxToMm / mmToPx) and a DISPLAY-ONLY fit
// clamp (displayFitScale) for designs bigger than the zone. Pricing and
// production read the untouched sizeMm.
//
// Pure + DOM-free so the whole file runs in the root vitest "worker"
// project (see previewGeometry.test.ts alongside).

export type OffsetMm = { xMm: number; yMm: number };

/** The calibrated zone in photo-mm space. x/y = top-left; cx/cy = centre
 *  (pre-computed because every caller wants it). */
export type ZoneRectMm = {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
};

const finite = (v: number): boolean => Number.isFinite(v);

/** Height (mm) of the whole photo frame, derived from the calibrated
 *  frame width and the photo's natural aspect ratio (naturalWidth /
 *  naturalHeight). Returns null when either input is degenerate - the
 *  caller should not render the overlay until the photo has measured. */
export function photoFrameHeightMm(
  photoWidthMm: number,
  photoAspect: number | null,
): number | null {
  if (!finite(photoWidthMm) || photoWidthMm <= 0) return null;
  if (photoAspect == null || !finite(photoAspect) || photoAspect <= 0) {
    return null;
  }
  return photoWidthMm / photoAspect;
}

/** Map the admin's normalised 0..1 calibration zone into photo-mm.
 *  Returns null until the photo aspect is known (no overlay without it). */
export function zoneRectMm(
  calibration: {
    photoWidthMm: number;
    zone: { x: number; y: number; w: number; h: number };
  },
  photoAspect: number | null,
): ZoneRectMm | null {
  const frameH = photoFrameHeightMm(calibration.photoWidthMm, photoAspect);
  if (frameH == null) return null;
  const frameW = calibration.photoWidthMm;
  const z = calibration.zone;
  if (![z.x, z.y, z.w, z.h].every(finite)) return null;
  const x = z.x * frameW;
  const y = z.y * frameH;
  const w = z.w * frameW;
  const h = z.h * frameH;
  if (w <= 0 || h <= 0) return null;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
}

/** DISPLAY-ONLY uniform scale that fits a blockW x blockH mm design
 *  inside the zone. 1 = true size; < 1 = the picked size is bigger than
 *  the zone can show, so the preview shrinks it (and the component shows
 *  a note). Never scales UP - a small design renders at true mm. */
export function displayFitScale(
  blockWMm: number,
  blockHMm: number,
  zone: { w: number; h: number },
): number {
  if (!finite(blockWMm) || !finite(blockHMm) || blockWMm <= 0 || blockHMm <= 0) {
    return 1;
  }
  const s = Math.min(1, zone.w / blockWMm, zone.h / blockHMm);
  return finite(s) && s > 0 ? s : 1;
}

/** Clamp a drag offset so an artWMm x artHMm design centred at
 *  (zone centre + offset) stays FULLY inside the zone. Art at or beyond
 *  the zone size on an axis pins that axis to 0 (centred). Non-finite
 *  input components clamp to 0. */
export function clampOffsetMm(
  offset: OffsetMm,
  artWMm: number,
  artHMm: number,
  zone: { w: number; h: number },
): OffsetMm {
  const maxX = Math.max(0, (zone.w - artWMm) / 2);
  const maxY = Math.max(0, (zone.h - artHMm) / 2);
  const cx = finite(offset.xMm) ? offset.xMm : 0;
  const cy = finite(offset.yMm) ? offset.yMm : 0;
  return {
    xMm: Math.min(maxX, Math.max(-maxX, cx)),
    yMm: Math.min(maxY, Math.max(-maxY, cy)),
  };
}

/** CSS-px -> photo-mm. The stage renders the photo frame edge-to-edge,
 *  so the scale is simply photoWidthMm / stage width. Returns 0 for a
 *  degenerate stage so a drag before first layout is a no-op. */
export function pxToMm(
  px: number,
  photoWidthMm: number,
  stagePxWidth: number,
): number {
  if (!finite(px) || !finite(photoWidthMm) || photoWidthMm <= 0) return 0;
  if (!finite(stagePxWidth) || stagePxWidth <= 0) return 0;
  return (px * photoWidthMm) / stagePxWidth;
}

/** photo-mm -> CSS-px. Inverse of pxToMm, same degenerate-input rules. */
export function mmToPx(
  mm: number,
  photoWidthMm: number,
  stagePxWidth: number,
): number {
  if (!finite(mm) || !finite(photoWidthMm) || photoWidthMm <= 0) return 0;
  if (!finite(stagePxWidth) || stagePxWidth <= 0) return 0;
  return (mm * stagePxWidth) / photoWidthMm;
}

/** Serialise a spot's preview offset for the `_spot_N_preview_offset`
 *  order property: "x:<xMm>,y:<yMm>" with whole-mm rounding. Returns
 *  null (emit nothing) when the offset is absent, non-finite, or rounds
 *  to dead centre - the property only exists when the customer actually
 *  moved the design. NO pricing semantics. */
export function previewOffsetProperty(
  offset: OffsetMm | null | undefined,
): string | null {
  if (!offset) return null;
  if (!finite(offset.xMm) || !finite(offset.yMm)) return null;
  const x = Math.round(offset.xMm);
  const y = Math.round(offset.yMm);
  if (x === 0 && y === 0) return null;
  return `x:${x},y:${y}`;
}
